
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BetaProgramData, UserMetric, UserContributor } from '@/types/betaProgram';

export const useBetaProgramData = () => {
  return useQuery({
    queryKey: ['beta-program-data'],
    queryFn: async (): Promise<BetaProgramData> => {
      // Fetch user stats
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*');

      const totalUsers = profiles?.length || 0;

      // Fetch user activity stats
      const { data: userActivityStats } = await supabase
        .from('user_activity_stats')
        .select('*')
        .order('last_login', { ascending: false })
        .limit(10);

      // Calculate active users
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const activeToday = userActivityStats?.filter(user => 
        user.last_activity && new Date(user.last_activity) >= todayStart
      ).length || 0;

      const activeThisWeek = userActivityStats?.filter(user => 
        user.last_activity && new Date(user.last_activity) >= weekStart
      ).length || 0;

      const newSignupsToday = profiles?.filter(profile => 
        new Date(profile.created_at || '') >= todayStart
      ).length || 0;

      const newSignupsThisWeek = profiles?.filter(profile => 
        new Date(profile.created_at || '') >= weekStart
      ).length || 0;

      // Fetch candidates and clients counts
      const { data: candidates } = await supabase
        .from('candidates')
        .select('id');

      const { data: clients } = await supabase
        .from('clients')
        .select('id');

      const { data: organizations } = await supabase
        .from('organizations')
        .select('id')
        .not('id', 'is', null);

      // Fetch system errors
      const { data: systemErrors } = await supabase
        .from('system_errors')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      const errorsToday = systemErrors?.filter(error => 
        new Date(error.created_at) >= todayStart
      ).length || 0;

      const errorsThisWeek = systemErrors?.filter(error => 
        new Date(error.created_at) >= weekStart
      ).length || 0;

      const unresolvedErrors = systemErrors?.filter(error => !error.resolved).length || 0;

      // Get most common error type
      const errorTypeCounts = systemErrors?.reduce((acc, error) => {
        acc[error.error_type] = (acc[error.error_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const topErrorType = Object.keys(errorTypeCounts).length > 0 
        ? Object.keys(errorTypeCounts).reduce((a, b) => errorTypeCounts[a] > errorTypeCounts[b] ? a : b)
        : null;

      // Fetch beta program metrics with user information
      const { data: betaMetrics } = await supabase
        .from('beta_program_metrics')
        .select('*')
        .gte('calculated_at', todayStart.toISOString());

      // Helper function to safely extract metadata properties
      const getTopContributors = (metadata: any): UserContributor[] => {
        if (metadata && typeof metadata === 'object' && Array.isArray(metadata.top_contributors)) {
          return metadata.top_contributors as UserContributor[];
        }
        return [];
      };

      const getUserEmail = (metadata: any): string => {
        if (metadata && typeof metadata === 'object' && typeof metadata.user_email === 'string') {
          return metadata.user_email;
        }
        return 'Unknown';
      };

      const getOrganizationId = (metadata: any): string | null => {
        if (metadata && typeof metadata === 'object' && typeof metadata.organization_id === 'string') {
          return metadata.organization_id;
        }
        return null;
      };

      // Process organization metrics with user contributors
      const candidateOrgMetrics = betaMetrics?.filter(m => m.metric_name === 'candidates_per_organization') || [];
      const clientOrgMetrics = betaMetrics?.filter(m => m.metric_name === 'clients_per_organization') || [];

      const topOrgsByCandidates = candidateOrgMetrics
        .sort((a, b) => b.metric_value - a.metric_value)
        .slice(0, 5)
        .map(metric => ({
          organization_id: metric.organization_id!,
          candidates_count: metric.metric_value,
          top_contributors: getTopContributors(metric.metadata)
        }));

      const topOrgsByClients = clientOrgMetrics
        .sort((a, b) => b.metric_value - a.metric_value)
        .slice(0, 5)
        .map(metric => ({
          organization_id: metric.organization_id!,
          clients_count: metric.metric_value,
          top_contributors: getTopContributors(metric.metadata)
        }));

      // Process user-level metrics
      const userCandidateMetrics = betaMetrics?.filter(m => m.metric_name === 'user_candidates_count') || [];
      const userClientMetrics = betaMetrics?.filter(m => m.metric_name === 'user_clients_count') || [];

      const topUsersByCandidates: UserMetric[] = userCandidateMetrics
        .sort((a, b) => b.metric_value - a.metric_value)
        .slice(0, 10)
        .map(metric => ({
          user_id: metric.user_id!,
          user_email: getUserEmail(metric.metadata),
          candidate_count: metric.metric_value,
          organization_id: getOrganizationId(metric.metadata)
        }));

      const topUsersByClients: UserMetric[] = userClientMetrics
        .sort((a, b) => b.metric_value - a.metric_value)
        .slice(0, 10)
        .map(metric => ({
          user_id: metric.user_id!,
          user_email: getUserEmail(metric.metadata),
          client_count: metric.metric_value,
          organization_id: getOrganizationId(metric.metadata)
        }));

      return {
        userStats: {
          total_users: totalUsers,
          active_today: activeToday,
          active_this_week: activeThisWeek,
          new_signups_today: newSignupsToday,
          new_signups_this_week: newSignupsThisWeek,
          total_candidates: candidates?.length || 0,
          total_clients: clients?.length || 0,
          active_organizations: organizations?.length || 0,
        },
        recentLogins: userActivityStats || [],
        errorSummary: {
          total_errors_today: errorsToday,
          total_errors_this_week: errorsThisWeek,
          unresolved_errors: unresolvedErrors,
          top_error_type: topErrorType,
          error_count: errorTypeCounts[topErrorType] || 0,
        },
        recentErrors: systemErrors || [],
        organizationMetrics: {
          top_organizations_by_candidates: topOrgsByCandidates,
          top_organizations_by_clients: topOrgsByClients,
        },
        userMetrics: {
          top_users_by_candidates: topUsersByCandidates,
          top_users_by_clients: topUsersByClients,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
};
